// PQRSD Test Bench — a one-page integration harness for the lab module.
//
// Runs the PQRSD suites in pqrsdTests.js against the live dev backend, auto-
// checks each assertion, and surfaces the actual stored data so you can verify
// it by eye. "Ejecutar todo" runs every suite and then PAUSES for confirmation
// before deleting the created records, so you can audit them (and the
// MANUAL/skipped checks) first.
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";

import { SUITES, SUITE_PERMS } from "./pqrsdTests";
import { cleanupLabPqrsd } from "./pqrsdLabHelpers";

const STATUS_COLOR = {
  passed: "success",
  failed: "error",
  skipped: "default",
  running: "warning",
  pending: "default",
};
const STATUS_LABEL = {
  passed: "OK",
  failed: "FALLÓ",
  skipped: "OMITIDO",
  running: "CORRIENDO",
  pending: "—",
};

const keyOf = (suite, test) => `${suite.key}.${test.key}`;
const detailHref = (rad) => `/pqrsd/admin/${encodeURIComponent(rad)}`;

export default function PqrsdTestBench() {
  const user = useSelector((state) => state.user.value);
  const perms = useMemo(() => user?.permissions || [], [user]);

  const [testEmail, setTestEmail] = useState(user?.email || "");
  const [results, setResults] = useState({}); // key -> { status, assertions, message, details, data, durationMs }
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cleanupInfo, setCleanupInfo] = useState(null);
  const [pendingCleanup, setPendingCleanup] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Radicados created during runs. Ref is the source of truth (survives
  // re-renders); the state array drives the audit list rendering.
  const registryRef = useRef(new Set());
  const [registered, setRegistered] = useState([]);

  const register = useCallback((rad) => {
    if (!rad || registryRef.current.has(rad)) return;
    registryRef.current.add(rad);
    setRegistered(Array.from(registryRef.current));
  }, []);

  const dropFromRegistry = useCallback((rad) => {
    registryRef.current.delete(rad);
    setRegistered(Array.from(registryRef.current));
  }, []);

  const buildCtx = useCallback(
    () => ({
      user,
      perms,
      testEmailBase: testEmail,
      register,
      getRegistered: () => Array.from(registryRef.current),
      state: {},
    }),
    [user, perms, testEmail, register]
  );

  const setResult = useCallback((key, patch) => {
    setResults((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const runOne = useCallback(
    async (suite, test, ctx) => {
      const key = keyOf(suite, test);
      setResult(key, { status: "running", assertions: [], message: null, details: null, data: null });
      const t0 = performance.now();
      try {
        const out = (await test.run(ctx)) || {};
        const durationMs = Math.round(performance.now() - t0);
        const assertions = out.assertions || [];
        let status = out.status;
        if (!status) status = assertions.some((a) => !a.ok) ? "failed" : "passed";
        setResult(key, {
          status,
          assertions,
          message: out.message || null,
          data: out.data || null,
          durationMs,
        });
      } catch (e) {
        const durationMs = Math.round(performance.now() - t0);
        setResult(key, {
          status: "failed",
          assertions: [],
          message: e?.message || String(e),
          details: e?.stack || null,
          durationMs,
        });
      }
    },
    [setResult]
  );

  // Generic runner used by "Ejecutar suite". Returns when the pass completes.
  const runSuites = useCallback(
    async (suites) => {
      if (running) return;
      setRunning(true);
      setProgress(0);
      setPendingCleanup(false);
      const ctx = buildCtx(); // shared state across this pass
      const total = suites.reduce((n, s) => n + s.tests.length, 0);
      let done = 0;
      for (const suite of suites) {
        for (const test of suite.tests) {
          await runOne(suite, test, ctx);
          done += 1;
          setProgress(Math.round((done / total) * 100));
        }
      }
      setRunning(false);
    },
    [running, buildCtx, runOne]
  );

  // "Ejecutar todo": run every suite, then pause for confirmation before the
  // final step (deleting the created records).
  const runAll = useCallback(async () => {
    if (running) return;
    await runSuites(SUITES);
    if (registryRef.current.size > 0) setPendingCleanup(true);
  }, [running, runSuites]);

  // Deletes the given radicados (or all registered) one at a time so a per-row
  // delete and a full cleanup share the same path. Always reports failures.
  const deleteRecords = useCallback(
    async (ids) => {
      const list = ids || Array.from(registryRef.current);
      setDeleting(true);
      let deleted = 0;
      const failed = [];
      for (const id of list) {
        const res = await cleanupLabPqrsd([id]);
        if (res.failed.length) failed.push(...res.failed);
        else {
          deleted += 1;
          dropFromRegistry(id);
        }
      }
      setDeleting(false);
      setPendingCleanup(false);
      setCleanupInfo({ deleted, failed, total: list.length });
    },
    [dropFromRegistry]
  );

  // --- Summary ---
  const summary = useMemo(() => {
    const counts = { passed: 0, failed: 0, skipped: 0, total: 0 };
    Object.values(results).forEach((r) => {
      counts.total += 1;
      if (r.status === "passed") counts.passed += 1;
      else if (r.status === "failed") counts.failed += 1;
      else if (r.status === "skipped") counts.skipped += 1;
    });
    return counts;
  }, [results]);

  const missingPerms = useMemo(() => {
    const out = [];
    Object.entries(SUITE_PERMS).forEach(([suiteKey, need]) => {
      const missing = need.filter((p) => !perms.includes(p));
      if (missing.length) out.push({ suiteKey, missing });
    });
    return out;
  }, [perms]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Banco de pruebas PQRSD
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ejecuta el módulo PQRSD de extremo a extremo contra el backend de
        desarrollo y verifica cada paso automáticamente.
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Esto crea radicados reales (etiquetados <strong>[LAB-TEST]</strong>) y
        puede enviar correos de confirmación. <strong>Ejecutar todo</strong> se
        detiene al final y pide confirmación antes de borrar, para que puedas
        auditar los registros. Cupo de intake: 20/IP y 5/correo por 10 min.
      </Alert>

      {/* Config bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              label="Correo de prueba"
              size="small"
              fullWidth
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              helperText="Se generan alias local+labtest-… para no chocar con el límite por correo."
            />
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={running || !testEmail}
              onClick={runAll}
            >
              Ejecutar todo
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={`Total: ${summary.total}`} />
            <Chip label={`OK: ${summary.passed}`} color="success" variant="outlined" />
            <Chip label={`Fallos: ${summary.failed}`} color="error" variant="outlined" />
            <Chip label={`Omitidos: ${summary.skipped}`} variant="outlined" />
          </Stack>

          {running && <LinearProgress variant="determinate" value={progress} />}

          {cleanupInfo && (
            <Alert
              severity={cleanupInfo.failed?.length ? "warning" : "success"}
              onClose={() => setCleanupInfo(null)}
            >
              Limpieza: eliminados {cleanupInfo.deleted}/{cleanupInfo.total}.
              {cleanupInfo.failed?.length
                ? ` ${cleanupInfo.failed.length} fallo(s) — revisa las reglas de Firestore.`
                : ""}
            </Alert>
          )}

          {missingPerms.length > 0 && (
            <Alert severity="info">
              Permisos faltantes (algunas suites se omitirán o fallarán):{" "}
              {missingPerms.map((m) => `${m.suiteKey} → ${m.missing.join(", ")}`).join(" · ")}
              . Ejecuta como PQRSD_SUPERVISOR o ADMIN para cobertura completa.
            </Alert>
          )}
        </Stack>
      </Paper>

      {/* Created-records audit list + confirmation gate */}
      {registered.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, borderColor: pendingCleanup ? "warning.main" : "divider" }}
        >
          {pendingCleanup && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Paso final: limpieza. Se crearon <strong>{registered.length}</strong>{" "}
              registro(s) de prueba. Audítalos (y valida las pruebas MANUAL/OMITIDO)
              y confirma para borrarlos.
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>
              Registros creados ({registered.length})
            </Typography>
            {pendingCleanup && (
              <Button size="small" onClick={() => setPendingCleanup(false)}>
                Mantener registros
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={deleting}
              onClick={() => deleteRecords()}
            >
              {pendingCleanup ? "Confirmar limpieza" : "Eliminar todos"}
            </Button>
          </Stack>
          {deleting && <LinearProgress sx={{ mb: 1 }} />}
          <Stack divider={<Divider flexItem />}>
            {registered.map((rad) => (
              <Stack key={rad} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: "monospace" }}>
                  {rad}
                </Typography>
                <Link
                  href={detailHref(rad)}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  Abrir <OpenInNewIcon fontSize="inherit" />
                </Link>
                <Tooltip title="Eliminar este registro">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={deleting}
                      onClick={() => deleteRecords([rad])}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Suites */}
      {SUITES.map((suite) => {
        const suiteResults = suite.tests.map((t) => results[keyOf(suite, t)]);
        const suiteFailed = suiteResults.some((r) => r?.status === "failed");
        const suiteRan = suiteResults.some((r) => r?.status);
        const suiteColor = suiteFailed ? "error" : suiteRan ? "success" : "default";
        return (
          <Accordion key={suite.key} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{suite.title}</Typography>
                <Chip
                  size="small"
                  color={suiteColor}
                  variant="outlined"
                  label={`${suite.tests.length} prueba(s)`}
                />
                <Button
                  size="small"
                  disabled={running || !testEmail}
                  onClick={(e) => {
                    e.stopPropagation();
                    runSuites([suite]);
                  }}
                >
                  Ejecutar suite
                </Button>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Stack divider={<Divider flexItem />}>
                {suite.tests.map((test) => {
                  const r = results[keyOf(suite, test)] || {};
                  const status = r.status || "pending";
                  return (
                    <Accordion key={test.key} disableGutters elevation={0} sx={{ "&:before": { display: "none" } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                          <Chip
                            size="small"
                            color={STATUS_COLOR[status]}
                            label={STATUS_LABEL[status]}
                            variant={status === "passed" || status === "failed" ? "filled" : "outlined"}
                          />
                          <Typography sx={{ flexGrow: 1 }}>{test.name}</Typography>
                          {typeof r.durationMs === "number" && (
                            <Typography variant="caption" color="text.secondary">
                              {r.durationMs} ms
                            </Typography>
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {test.description}
                        </Typography>
                        {r.message && (
                          <Alert
                            severity={status === "skipped" ? "info" : status === "failed" ? "error" : "success"}
                            sx={{ mb: 1 }}
                          >
                            {r.message}
                          </Alert>
                        )}
                        {(r.assertions || []).map((a, i) => (
                          <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.25 }}>
                            {a.ok ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <CancelIcon color="error" fontSize="small" />
                            )}
                            <Typography variant="body2">
                              <strong>{a.label}</strong>
                              {a.detail ? ` — ${a.detail}` : ""}
                            </Typography>
                          </Stack>
                        ))}
                        {status === "skipped" && !r.message && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <RemoveCircleOutlineIcon fontSize="small" color="disabled" />
                            <Typography variant="body2" color="text.secondary">
                              Omitido.
                            </Typography>
                          </Stack>
                        )}
                        {/* Stored-data inspector: shows exactly what landed in
                            Firestore (annexes, events/logs, assignees, flows). */}
                        {r.data &&
                          Object.entries(r.data).map(([label, value]) => (
                            <Box key={label} sx={{ mt: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {label}
                              </Typography>
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  p: 1,
                                  bgcolor: "action.hover",
                                  borderRadius: 1,
                                  fontSize: 11,
                                  overflow: "auto",
                                  maxHeight: 300,
                                }}
                              >
                                {JSON.stringify(value, null, 2)}
                              </Box>
                            </Box>
                          ))}
                        {r.details && (
                          <Box
                            component="pre"
                            sx={{
                              mt: 1,
                              p: 1,
                              bgcolor: "action.hover",
                              borderRadius: 1,
                              fontSize: 11,
                              overflow: "auto",
                            }}
                          >
                            {r.details}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Container>
  );
}
